import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterSelect } from '../../shared/table/FilterSelect';
import { useAccountGeneralTab } from '../hooks';

export const AccountGeneralTabContent = () => {
    const dispatch = useDispatch();
    const { id: companyId } = useSelector((state) => state.company);
    const { token } = useSelector((state) => state.auth);

    const {
        formData,
        logoFile,
        loading,
        saving,
        error,
        isDirty,
        activeLogoUrl,
        selectedLogoSummary,
        logoInputRef,
        timezoneOptions,
        handleFieldChange,
        handleTimezoneChange,
        handleOpenLogoPicker,
        handleLogoFileChange,
        handleRemoveLogo,
        handleSubmit,
    } = useAccountGeneralTab({
        token,
        companyId,
        dispatch,
    });

    return (
        <div className="profile-details mb-1">
            <div className="mb-4 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                <div>
                    <h5 className="fw-bold mb-1">Datos generales de la compañía</h5>
                    {isDirty ? <div className="account-general-dirty">Hay cambios sin guardar</div> : null}
                </div>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving || loading}>
                    {saving ? (
                        <span className="d-inline-flex align-items-center gap-2">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Guardando...
                        </span>
                    ) : 'Guardar'}
                </button>
            </div>

            {error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            {loading ? (
                <div className="text-muted">Cargando configuración general...</div>
            ) : (
                <div className="d-flex flex-column gap-4">
                    <section className="account-general-section">
                        <div className="account-general-section__header">
                            <h6 className="fw-semibold mb-1">Identidad de la empresa</h6>
                            <p className="text-muted mb-0">Actualiza el nombre, descripción, sitio web y logo del tenant.</p>
                        </div>

                        <div className="row g-3 align-items-start">
                            <div className="col-12 col-xl-4">
                                <input
                                    ref={logoInputRef}
                                    type="file"
                                    className="d-none"
                                    accept="image/png,image/jpeg,image/svg+xml"
                                    onChange={handleLogoFileChange}
                                />

                                <button type="button" className="account-general-logo" onClick={handleOpenLogoPicker}>
                                    {activeLogoUrl ? (
                                        <img src={activeLogoUrl} alt="Logo de la empresa" className="account-general-logo__image" />
                                    ) : (
                                        <i className="feather-briefcase text-primary fs-1" />
                                    )}
                                </button>

                                <div className="mt-3">
                                    <div className="fw-semibold">Logo del tenant</div>
                                    <div className="text-muted fs-12">PNG, JPG o SVG. Máximo 2 MB.</div>
                                    {selectedLogoSummary ? (
                                        <div className="text-muted fs-12 mt-2">{selectedLogoSummary}</div>
                                    ) : null}
                                    {(activeLogoUrl || logoFile) ? (
                                        <button type="button" className="btn btn-link p-0 mt-2" onClick={handleRemoveLogo}>
                                            Quitar logo actual
                                        </button>
                                    ) : null}
                                </div>
                            </div>

                            <div className="col-12 col-xl-8">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label" htmlFor="company-name">Nombre de la empresa</label>
                                        <input
                                            id="company-name"
                                            name="name"
                                            className="form-control"
                                            value={formData.name}
                                            onChange={handleFieldChange}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <label className="form-label" htmlFor="company-description">Descripción</label>
                                            <span className="text-muted fs-12">{String(formData.description || '').length}/300</span>
                                        </div>
                                        <textarea
                                            id="company-description"
                                            name="description"
                                            className="form-control"
                                            rows={4}
                                            maxLength={300}
                                            value={formData.description}
                                            onChange={handleFieldChange}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <label className="form-label" htmlFor="company-website">Sitio web</label>
                                        <input
                                            id="company-website"
                                            name="website"
                                            className="form-control"
                                            placeholder="https://"
                                            value={formData.website}
                                            onChange={handleFieldChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="account-general-section">
                        <div className="account-general-section__header">
                            <h6 className="fw-semibold mb-1">Datos de contacto del responsable</h6>
                            <p className="text-muted mb-0">Esta información se usará para contacto administrativo del tenant.</p>
                        </div>

                        <div className="row g-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label" htmlFor="company-contact-name">Nombre del responsable</label>
                                <input
                                    id="company-contact-name"
                                    name="contactName"
                                    className="form-control"
                                    value={formData.contactName}
                                    onChange={handleFieldChange}
                                />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label" htmlFor="company-contact-email">Email de contacto</label>
                                <input
                                    id="company-contact-email"
                                    name="contactEmail"
                                    type="email"
                                    className="form-control"
                                    value={formData.contactEmail}
                                    onChange={handleFieldChange}
                                />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label" htmlFor="company-contact-phone">Teléfono</label>
                                <input
                                    id="company-contact-phone"
                                    name="contactPhone"
                                    className="form-control"
                                    value={formData.contactPhone}
                                    onChange={handleFieldChange}
                                />
                            </div>
                        </div>
                    </section>

                    <section className="account-general-section">
                        <div className="account-general-section__header">
                            <h6 className="fw-semibold mb-1">Configuración regional</h6>
                            <p className="text-muted mb-0">Se usa para calcular los SLA y reportes del sistema.</p>
                        </div>

                        <div className="row g-3">
                            <div className="col-12 col-lg-7">
                                <label className="form-label">Zona horaria</label>
                                <FilterSelect
                                    value={formData.timezone}
                                    options={timezoneOptions}
                                    searchable
                                    searchPlaceholder="Buscar zona horaria..."
                                    onChange={handleTimezoneChange}
                                />
                                <div className="text-muted fs-12 mt-2">Se usa para calcular los SLA y reportes del sistema</div>
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};
